'use client';
import React from "react";
import { useQuery } from '@tanstack/react-query';
import { http } from '@/lib/http';
import { useDict } from "@/app/[lang]/providers/DictionaryProvider";
import TableLayout from "@/components/common/TableLayout";
import DownloadIconLink from "@/components/common/button/DownloadIconLink";

interface AnnualReportCategoryPageProps {
  category: string;
  title: string;
  lang: string;
}

interface AnnualReport {
  id: number;
  title: string;
  year: string;
  file_path: string;
  category: string;
}

interface PaginatedResponse {
  data: AnnualReport[];
  meta?: {
    current_page: number;
    total: number;
    per_page: number;
  };
}

export default function AnnualReportCategoryPage({ category, title, lang }: AnnualReportCategoryPageProps) {
  const dict = useDict();
  const [selectedYear, setSelectedYear] = React.useState<number | undefined>();
  const [selectedMonth, setSelectedMonth] = React.useState<number | undefined>();
  
  const { data } = useQuery<PaginatedResponse>({
    queryKey: ['annual-reports-category', category, lang, selectedYear, selectedMonth],
    enabled: !!category,
    queryFn: async () => {
      const params = new URLSearchParams({ lang, category });
      
      if (selectedYear !== undefined) {
        params.append('year', selectedYear.toString());
      }
      
      if (selectedMonth !== undefined) {
        params.append('month', selectedMonth.toString());
      }
      
      const res = await http.get(`/api/laporan-tahunans?${params.toString()}`);
      return res.data;
    },
  });

  const handleDateFilterChange = (year?: number, month?: number) => {
    setSelectedYear(year);
    setSelectedMonth(month);
  };

  return (
    <TableLayout
      dateFilterType="year"
      onDateFilterChange={handleDateFilterChange}
      data={data?.data}
      title={title}
      columns={[
        { id: 'year', header: dict.common.year, accessor: 'year' },
        { id: 'title', header: dict.common.title, accessor: 'title' },
        {
          id: 'downloadUrl',
          header: '',
          accessor: 'file_path',
          cell: (value: string) => <DownloadIconLink href={value} ariaLabel={dict.button.download} />,
        },
      ]}
    />
  );
}
